import { useEffect, useState } from "react"

import { LoadingOutlined } from "@ant-design/icons"
import { Avatar, Dropdown, Flex, Popover, Space, Switch, Tooltip, Typography } from "antd"
import cls from "classname"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { Link, useLocation } from "react-router-dom"

import Button from "../../Components/Button"
import Private, { INCOGNITO_MASK } from "../../Components/Private"
import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import Icon from "../../Icon"
import Images from "../../Images"
import { isIncognitoAvailable, selectIncognito } from "../../Utils/incognito"
import { superAdminRoleId } from "../../Utils/permission"
import { useIsReferralEnabled } from "../Referrals/hooks/useIsReferralEnabled"
import SettingCreators from "../Setting/reducer"
import UserCreators from "../User/reducer"
import actions from "./actions"

import "./styles.less"

const { Text } = Typography

const MenuComponent = ({
  fetching,
  isAdmin,
  user,
  logout,
  theme,
  type,
  isShowSideBar,
  languages,
  isMobile,
  showText = false,
  bannerInfo,
  ...props
}) => {
  const { t } = useTranslation()
  const location = useLocation()
  const [themePopoverOpen, setThemePopoverOpen] = useState(false)
  const [langPopoverOpen, setLangPopoverOpen] = useState(false)
  const balanceEnabled =
    !user?.is_loyalty_billing || (user?.is_loyalty_billing && bannerInfo?.balance?.amount >= 0)
  const referralEnabled = useIsReferralEnabled()
  const externalDemoMode = useExternalDemoMode()
  const isManagerAllowed = user?.is_manager_allowed
  const isLoyaltyBilling = user?.is_loyalty_billing
  const name = user?.first_name + " " + user?.last_name
  const language = user?.language
  const getLanguage = () => languages?.find((el) => el.value === language)?.name
  const [menuOpen, setMenuOpen] = useState(false)
  const [openLanguage, setOpenLanguage] = useState(false)
  useEffect(() => {
    if (!!user && !location?.pathname?.includes("account")) props.getUserInfo()
    if (!languages.length) props.getLanguagesList()
  }, [])

  const handleChangeTheme = (e, newTheme) => {
    e.stopPropagation()
    if (theme === newTheme) return
    props.changeTheme(newTheme)
    setThemePopoverOpen(false)
  }
  const handleOpenChangeLanguage = (open) => {
    setOpenLanguage(open)
  }

  const handleChangeLanguage = (e, lang) => {
    e.stopPropagation()
    props.changeLang(lang)
    setLangPopoverOpen(false)
  }

  const handleChangeMenuOpen = (open, info) => {
    if (info?.source === "menu" && open) return
    if (!open) {
      setThemePopoverOpen(false)
      setLangPopoverOpen(false)
      requestAnimationFrame(() => setMenuOpen(false))
      return
    }
    setMenuOpen(open)
  }

  const handleMenuItemClick = ({ key }) => {
    if (key !== "language" && key !== "switch_theme" && key !== "incognito") {
      setMenuOpen(false)
    }
  }

  const handleToggleIncognito = (checked) => {
    props.setIncognito(checked)
  }

  // In Incognito mode a profile photo (and the initials that stand in for it) is sensitive too,
  // so both avatars fall back to a plain coloured circle.
  const incognito = !!props.incognito
  const showUserPhoto = !incognito && !!user?.avatar
  const userInitials = incognito ? null : `${user?.first_name?.[0]}${user?.last_name?.[0]}`

  const avatar = (
    <Avatar
      className="user_avatar"
      size={40}
      style={{
        backgroundColor: showUserPhoto ? "transparent" : "#225AEA",
        borderRadius: "20px",
        border: `1px solid ${theme === "dark" ? "#1D1D1D" : "#FFFFFF"}`,
        fontSize: 14
      }}
      {...(showUserPhoto && { src: user?.avatar })}
    >
      {!showUserPhoto ? userInitials : null}
    </Avatar>
  )

  const avatarSidebarBottom = (
    <Avatar
      className="user_avatar"
      size={38}
      style={{
        backgroundColor: showUserPhoto ? "transparent" : "#225AEA",
        borderRadius: "50%",
        border: `1px solid ${theme === "dark" ? "#1D1D1D" : "#D0D0D4"}`,
        fontSize: 14,
        flexShrink: 0
      }}
      {...(showUserPhoto && { src: user?.avatar })}
    >
      {!showUserPhoto ? userInitials : null}
    </Avatar>
  )

  const avatarSidebarAccountManager = (
    <Avatar
      className="user_avatar"
      size={38}
      style={{
        backgroundColor: !incognito && user?.account_manager ? "transparent" : "#707BA0",
        borderRadius: "50%",
        border: `1px solid ${theme === "dark" ? "#27272A" : "#D0D0D4"}`,
        fontSize: 14
      }}
      {...(!incognito && { src: Images.account_manager })}
    >
      {!incognito && !user?.account_manager?.avatar
        ? `${user?.account_manager?.first_name?.[0] || "A"}${
            user?.account_manager?.last_name?.[0] || "M"
          }`
        : null}
    </Avatar>
  )

  const content = (
    <Flex vertical={true} gap={4}>
      <Button
        size="large"
        type="text"
        icon={<Icon type={"moon"} style={{ color: "#7A7A83" }} />}
        style={{ width: 150, fontWeight: 400 }}
        className={"flex dark-hover-100"}
        onClick={(e) => handleChangeTheme(e, "dark")}
      >
        {t("Dark")}
        {theme === "dark" && (
          <Icon type="circle_check" style={{ fontSize: 16, marginLeft: "auto" }} />
        )}
      </Button>
      <Button
        size="large"
        type="text"
        icon={<Icon type={"sun"} style={{ color: "#7A7A83" }} />}
        style={{ width: 150, fontWeight: 400 }}
        className={"flex dark-hover-100"}
        onClick={(e) => handleChangeTheme(e, "light")}
      >
        {t("Light")}
        {theme === "light" && (
          <Icon type="circle_check" style={{ fontSize: 16, marginLeft: "auto" }} />
        )}
      </Button>
    </Flex>
  )

  let initItems = [
    { type: "divider" },
    {
      key: 1,
      title: "Personal settings",
      label: (
        <Link to={"/settings/my_profile"}>
          <Icon type={"settings"} />
          {t("Personal settings")}
        </Link>
      )
    },
    {
      key: 2,
      title: "Help & Support",
      label: (
        <Flex align="center" onClick={() => window?.Intercom("show")}>
          <Icon type={"support"} />
          {t("Help & Support")}
        </Flex>
      )
    },
    { type: "divider" }
  ]
  const initItemsAdmin = [
    /*{
      key: 1,
      title: 'Settings',
      label: <Link to={'/settings/my_profile'}>{t('Settings')}</Link>,
    },
    {type: 'divider'},*/
  ]

  const contentLanguage = (
    <Flex vertical={true} gap={4}>
      {languages?.map((el) => {
        return (
          <Button
            size="large"
            type="text"
            style={{ width: 150, fontWeight: 400 }}
            className={"flex dark-hover-100"}
            onClick={(e) => handleChangeLanguage(e, el?.value)}
            key={el?.value}
          >
            {t(`${el?.name}`)}
          </Button>
        )
      })}
    </Flex>
  )

  const itemsAdmin = [
    !isAdmin && {
      key: 0,
      title: "User info",
      label: (
        <Space className={"user_info"}>
          {avatar}
          <Flex vertical={true}>
            <Text ellipsis={{ tooltip: props.incognito ? INCOGNITO_MASK : name }} strong>
              <Private>{name}</Private>
            </Text>
            <Text
              ellipsis={{
                tooltip: props.incognito ? INCOGNITO_MASK : user?.email
              }}
              type="secondary"
            >
              <Private>{user?.email}</Private>
            </Text>
          </Flex>
        </Space>
      )
    },
    // {type: 'divider'},
    // {
    //   key: 1,
    //   title: 'Personal settings',
    //   label: <Link to={'/settings/my_profile'}><Icon type={'settings'}/>{t('Personal settings')}</Link>,
    // },
    // (balanceEnabled && {
    //   key: 2,
    //   title: 'Balance',
    //   label: <Link to={'/settings/balance'}>
    //     <Icon type={'card'}/>
    //     {t('Balance')}
    //     <Flex gap={4} align="center" className="mark-new" style={{marginLeft: 'auto'}}>
    //       <Text style={{color: 'currentColor', textTransform: 'uppercase'}}>{t('New')}</Text>
    //     </Flex>
    //   </Link>,
    // }),
    // {type: 'divider'},
    user?.role?.id === superAdminRoleId && {
      key: "admin_users",
      title: "Admin users",
      label: (
        <Link to={"/admin_users"}>
          <Icon type={"two_users"} />
          {t("Admin users")}
        </Link>
      )
    },
    {
      key: "switch_theme",
      title: "Switch theme",
      label: (
        <div onClick={(e) => e.stopPropagation()}>
          <Popover
            placement={isMobile ? "bottom" : "leftTop"}
            content={content}
            arrow={false}
            getPopupContainer={() => document.getElementById("main-layout")}
            destroyTooltipOnHide={true}
            open={menuOpen && themePopoverOpen}
            onOpenChange={setThemePopoverOpen}
          >
            <Flex align="center">
              <Icon type={theme === "dark" ? "moon" : "sun"} />
              {t("Theme")}

              <Flex gap={4} style={{ marginLeft: "auto" }} align={"center"}>
                <Text style={{ textTransform: "capitalize" }}>{t(theme)}</Text>
                <Icon
                  type={"chevron_right_small"}
                  style={{ fontSize: 20, margin: "0", color: "currentColor" }}
                />
              </Flex>
            </Flex>
          </Popover>
        </div>
      )
    },
    {
      key: "security",
      title: "Security",
      label: (
        <Link to={"/security"}>
          <Icon type={"security"} />
          {t("Security")}
        </Link>
      )
    },
    { type: "divider" },
    {
      key: 8,
      title: "Log out",
      label: (
        <div className="user_logout" onClick={logout}>
          <Icon type={"logout"} />
          {t("Logout")}
        </div>
      )
    }
  ]

  const email = "roudy@chargeback.io"

  const meetingLink = "http://calendly.com/roudy-chargeback"
  const whatsAppLink = "https://wa.me/+447380593325"
  const manager_first_name = "Roudy"
  const manager_last_name = "Hanna"

  const managerContacts = [
    {
      id: "whats_app",
      icon_type: "whatsap",
      onClick: () => window.open(whatsAppLink),
      tooltipMessage: "Contact on WhatsApp"
    },
    {
      id: "calendar_days",
      icon_type: "calendar_days",
      onClick: () => window.open(meetingLink),
      tooltipMessage: "Schedule a call"
    },
    {
      id: "gmail",
      icon_type: "Google",
      onClick: () => (window.location.href = `mailto:${email}?subject=Help`),
      tooltipMessage: "Contact by email"
    }
  ]

  const itemsUser = [
    {
      key: 1,
      title: "Settings",
      label: (
        <Link to={"/settings/my_profile"}>
          <Icon type={"settings"} />
          {t("Settings")}
        </Link>
      )
    },
    user?.show_perks && {
      key: "perks",
      title: "Perks & Benefits",
      label: (
        <Link to={"/perks"}>
          <Icon type={"gift"} />
          {t("Perks & Benefits")}
        </Link>
      )
    },
    referralEnabled && {
      key: 2,
      title: "Referral program",
      label: (
        <Link to={"/settings/referrals"}>
          <Icon type={"referal"} />
          {t("Referral program")}
        </Link>
      )
      // disabled: externalDemoMode,
    },
    balanceEnabled && {
      key: 3,
      title: "Balance",
      label: (
        <Link to={"/settings/balance"}>
          <Icon type={"card"} />
          {t("Balance")}
          <Flex gap={4} align="center" className="mark-new" style={{ marginLeft: "auto" }}>
            <Text style={{ color: "currentColor" }}>{t("NEW")}</Text>
          </Flex>
        </Link>
      )
    },
    {
      key: "language",
      title: "Language",
      disabled: externalDemoMode,
      label: externalDemoMode ? (
        <Flex align="center">
          <Icon type={"language"} />
          {t("Language")}

          <Flex gap={4} style={{ marginLeft: "auto" }} align={"center"}>
            <Text style={{ textTransform: "capitalize" }}>{t(`${getLanguage()}`)}</Text>
            <Icon
              type={"chevron_right_small"}
              style={{ fontSize: 20, margin: "0", color: "currentColor" }}
            />
          </Flex>
        </Flex>
      ) : (
        <div onClick={(e) => e.stopPropagation()}>
          <Popover
            placement={isMobile ? "bottom" : "leftTop"}
            content={contentLanguage}
            arrow={false}
            getPopupContainer={() => document.getElementById("main-layout")}
            destroyTooltipOnHide={true}
            open={menuOpen && langPopoverOpen}
            onOpenChange={setLangPopoverOpen}
          >
            <Flex align="center">
              <Icon type={"language"} />
              {t("Language")}

              <Flex gap={4} style={{ marginLeft: "auto" }} align={"center"}>
                <Text style={{ textTransform: "capitalize" }}>{t(`${getLanguage()}`)}</Text>
                <Icon
                  type={"chevron_right_small"}
                  style={{ fontSize: 20, margin: "0", color: "currentColor" }}
                />
              </Flex>
            </Flex>
          </Popover>
        </div>
      )
    },
    {
      key: "switch_theme",
      title: "Switch theme",
      label: (
        <div onClick={(e) => e.stopPropagation()}>
          <Popover
            placement={isMobile ? "bottom" : "leftTop"}
            content={content}
            arrow={false}
            getPopupContainer={() => document.getElementById("main-layout")}
            destroyTooltipOnHide={true}
            open={menuOpen && themePopoverOpen}
            onOpenChange={setThemePopoverOpen}
          >
            <Flex align="center">
              <Icon type={theme === "dark" ? "moon" : "sun"} />
              {t("Theme")}

              <Flex gap={4} style={{ marginLeft: "auto" }} align={"center"}>
                <Text style={{ textTransform: "capitalize" }}>{t(theme)}</Text>
                <Icon
                  type={"chevron_right_small"}
                  style={{ fontSize: 20, margin: "0", color: "currentColor" }}
                />
              </Flex>
            </Flex>
          </Popover>
        </div>
      )
    },
    // Standard mode only — never offered in the admin panel, Demo / External demo or Onboarding.
    props.incognitoAvailable && {
      key: "incognito",
      title: "Incognito mode",
      label: (
        <div className="user_incognito" onClick={(e) => e.stopPropagation()}>
          <Icon type={"eye_invisible"} />
          <span className="user_incognito-label">{t("Incognito mode")}</span>
          <Switch size="small" checked={!!props.incognito} onChange={handleToggleIncognito} />
        </div>
      )
    },
    {
      key: 6,
      title: "Log out",
      label: (
        <div className="user_logout" onClick={externalDemoMode ? null : logout}>
          <Icon type={"logout"} />
          {t("Logout")}
        </div>
      ),
      disabled: externalDemoMode
    },
    isManagerAllowed && {
      key: 7,
      label: (
        <Flex gap={12} vertical className="account-manager-block">
          <Flex gap={12}>
            {avatarSidebarAccountManager}
            <Flex vertical>
              <Text>
                <Private>{manager_first_name + " " + manager_last_name}</Private>
              </Text>
              <Text type="secondary">{t("Account Manager")}</Text>
            </Flex>
          </Flex>
          <Flex gap={8}>
            {managerContacts?.map((el) => (
              <Tooltip
                placement="top"
                title={!isMobile ? el.tooltipMessage : null}
                overlayClassName="sidebar-tooltip"
              >
                <Button
                  className={"flex center small-space contact-btn"}
                  block
                  onClick={el?.onClick}
                >
                  <Icon role="icon" type={el?.icon_type} style={{ margin: 0 }} />
                </Button>
              </Tooltip>
            ))}
          </Flex>
        </Flex>
      )
      // disabled: true, // чтобы не было hover-эффектов
    }
  ]

  let items = isAdmin ? itemsAdmin : itemsUser

  if (type === "sidebar-bottom-menu") {
    return (
      <div className="menu-dropdown-wrap-sidebar-bottom">
        <Dropdown
          overlayClassName={"custom-menu sidebar-bottom-menu"}
          getPopupContainer={() => document.getElementById("main-layout")}
          menu={{ items, onClick: handleMenuItemClick }}
          trigger={["click"]}
          placement="topRight"
          overlayStyle={{ minWidth: 268 }}
          open={menuOpen}
          onOpenChange={handleChangeMenuOpen}
        >
          <div
            className={cls("menu-trigger-block", {
              "small-sidebar": !isShowSideBar && !isMobile
            })}
            data-tour="account-settings"
          >
            <Flex gap={12} justify="center" style={{ overflow: "hidden" }}>
              {avatarSidebarBottom}
              {isShowSideBar && !isMobile ? (
                <Flex vertical style={{ overflow: "hidden" }}>
                  <Text
                    style={{
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden"
                    }}
                  >
                    <Private>{name}</Private>
                  </Text>
                  <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
                    {t("Account settings")}
                  </Text>
                </Flex>
              ) : null}
            </Flex>
            {(isShowSideBar || showText) && !isMobile ? <Icon type="arrows_opposite" /> : null}
          </div>
        </Dropdown>
      </div>
    )
  } else {
    return (
      <div className="menu-dropdown-wrap">
        <Dropdown
          open={menuOpen}
          onOpenChange={handleChangeMenuOpen}
          overlayClassName={"custom-menu"}
          getPopupContainer={() => document.getElementById("main-layout")}
          menu={{ items, onClick: handleMenuItemClick }}
          trigger={["click"]}
          placement="bottomLeft"
          overlayStyle={{ minWidth: 268 }}
        >
          <div className="menu-trigger-block">
            {avatar}
            {fetching ? <LoadingOutlined /> : <Icon type="chevron_down_small" />}
          </div>
        </Dropdown>
      </div>
    )
  }
}

MenuComponent.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    avatar: PropTypes.string
  }),
  logout: PropTypes.func.isRequired
}

const mapStateToProps = (state) => ({
  fetching: state.auth.fetching,
  user: state.auth.userInfo,
  isAdmin: state.auth.isAdmin,
  refreshToken: state.auth.refreshToken,
  theme: state.nav.theme,
  incognito: selectIncognito(state),
  incognitoAvailable: isIncognitoAvailable(state),
  isShowSideBar: state.nav.isShowSideBar,
  isMobile: state.nav.isMobile,
  languages: state.setting.languages,
  bannerInfo: state.user.bannerInfo
})

const mapDispatchToProps = (dispatch) => ({
  getUserInfo: () => dispatch(actions.getUserInfo()),
  changeTheme: (theme) => dispatch(actions.changeTheme("theme", theme)),
  setIncognito: (value) => dispatch(actions.changeTheme("incognito", value)),
  logout: () => dispatch(actions.logout()),
  changeLang: (lang) => dispatch(UserCreators.changeLanguageRequest(lang)),
  getLanguagesList: () => dispatch(SettingCreators.getLanguagesListRequest())
})

export default connect(mapStateToProps, mapDispatchToProps)(MenuComponent)
